import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import type { Connection, PublicKey, Transaction, TransactionSignature } from '@solana/web3.js';
import {
  WalletReadyState,
  type Adapter,
  type MessageSignerWalletAdapter,
  type SendTransactionOptions,
  type WalletName,
} from '@solana/wallet-adapter-base';
import {
  linkWallet,
  listWallets,
  removeWallet,
  setPrimaryWallet as setPrimaryWalletApi,
  type SavedWallet,
} from '../services/walletService';
import { catalogEntryForAdapterName, catalogEntryForProviderSlug, providerLabel } from '../config/walletCatalog';
import { buildOwnershipMessage } from '../utils/buildOwnershipMessage';
import { shortenAddress } from '../config/cwyConfig';
import { signWalletOwnershipMessage } from '../utils/signWalletOwnershipMessage';
import { encodeSignatureForApi } from '../utils/verifyOwnershipProof';
import { prepareWalletConnection } from '../utils/prepareWalletConnection';
import { getCwyWalletAdapter } from '../providers/solanaAdapters';
import { adapterSupportsSending, sendTransactionWithAdapter } from '../utils/adapterSendTransaction';
import { validateWalletNetwork } from '../utils/walletNetworkCheck';

export type WalletLinkStatus = 'none' | 'wallet_only' | 'linked' | 'mismatch';

export type WalletTransactionSigner = {
  publicKey: PublicKey;
  sendTransaction: (
    transaction: Transaction,
    connection: Connection,
    options?: SendTransactionOptions
  ) => Promise<TransactionSignature>;
};

function pickPrimary(wallets: SavedWallet[]): SavedWallet | null {
  return wallets.find((w) => w.is_primary) ?? wallets[0] ?? null;
}

function isMessageSignerAdapter(
  adapter: unknown
): adapter is MessageSignerWalletAdapter {
  return (
    typeof adapter === 'object' &&
    adapter !== null &&
    'signMessage' in adapter &&
    typeof (adapter as MessageSignerWalletAdapter).signMessage === 'function'
  );
}

function formatConnectError(error: unknown): { title: string; detail?: string } {
  const err = error as {
    code?: number;
    name?: string;
    message?: string;
    response?: { data?: { message?: string } };
  };

  if (err.code === 4001 || err.message?.toLowerCase().includes('user rejected')) {
    return { title: 'Connection cancelled', detail: 'You declined the request in your wallet.' };
  }

  const apiMessage = err.response?.data?.message;
  const rawMessage = apiMessage || err.message || '';
  const lowerMsg = rawMessage.toLowerCase();

  if (
    lowerMsg.includes('wallet action failed') ||
    lowerMsg.includes('failed to connect') ||
    lowerMsg.includes('nightly connect failed')
  ) {
    return {
      title: 'Nightly connection failed',
      detail:
        'Nightly rejected the connect/sign request. Unlock Nightly, set Solana network to Mainnet (or Devnet to match the app), approve the popup, then try again. If it still fails, update the Nightly extension.',
    };
  }

  // Network mismatch — only when the message clearly indicates cluster mismatch
  // (do not treat any mention of "mainnet"/"devnet" as an error — false positives).
  const lower = rawMessage.toLowerCase();
  const isNetworkMismatch =
    lower.includes('network mismatch') ||
    lower.includes('switch to the correct network') ||
    lower.includes('wrong network') ||
    (lower.includes('devnet') && lower.includes('mainnet')) ||
    err.code === 4901;
  if (isNetworkMismatch) {
    return {
      title: 'Wrong network in your wallet',
      detail:
        'Your wallet extension is on a different Solana network than this app. ' +
        'Open your wallet (Phantom / Solflare / Brave / Nightly) → Settings → Network, ' +
        'match Mainnet or Devnet to the app, then try again.',
    };
  }

  if (rawMessage.toLowerCase().includes('wallet ownership')) {
    return { title: 'Failed to connect', detail: rawMessage };
  }

  return {
    title: 'Failed to connect',
    detail:
      rawMessage ||
      'Could not connect to your wallet. Unlock the extension and try again.',
  };
}

export function useSolanaWallets() {
  const { connection } = useConnection();
  const {
    wallets: adapterWallets,
    wallet: activeAdapter,
    publicKey,
    connected,
    connecting: adapterConnecting,
    select,
    disconnect: adapterDisconnect,
    sendTransaction,
  } = useWallet();

  const [savedWallets, setSavedWallets] = useState<SavedWallet[]>([]);
  const [primaryWallet, setPrimaryWallet] = useState<SavedWallet | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectingWallet, setConnectingWallet] = useState<WalletName | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);
  const [removingWalletId, setRemovingWalletId] = useState<string | null>(null);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerError, setPickerError] = useState<{ title: string; detail?: string } | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const connectedAddress = useMemo(() => publicKey?.toBase58() ?? null, [publicKey]);

  const loadSavedWallets = useCallback(async () => {
    setLoadingSaved(true);
    try {
      const { wallets, primary } = await listWallets();
      setSavedWallets(wallets);
      setPrimaryWallet(primary ?? pickPrimary(wallets));
    } catch {
      setSavedWallets([]);
      setPrimaryWallet(null);
    } finally {
      setLoadingSaved(false);
    }
  }, []);

  useEffect(() => {
    void loadSavedWallets();
  }, [loadSavedWallets]);

  const linkStatus: WalletLinkStatus = useMemo(() => {
    const primaryAddress = primaryWallet?.address;
    if (!primaryAddress) {
      return connectedAddress ? 'wallet_only' : 'none';
    }
    if (connectedAddress && connectedAddress !== primaryAddress) {
      return 'mismatch';
    }
    return 'linked';
  }, [primaryWallet?.address, connectedAddress]);

  const savedWallet = primaryWallet;

  const connectWithAdapter = useCallback(
    async (adapterName: WalletName) => {
      setConnecting(true);
      setConnectingWallet(adapterName);
      setError(null);
      setPickerError(null);
      setWarning(null);
      setSuccessMessage(null);

      let adapterInstance: Adapter | null = null;

      try {
        const adapter = getCwyWalletAdapter(adapterName);
        if (!adapter) {
          throw new Error('Wallet adapter not found');
        }

        adapterInstance = adapter;

        if (adapter.readyState === WalletReadyState.NotDetected) {
          const entry = catalogEntryForAdapterName(adapterName);
          throw new Error(
            entry?.detectHint ??
              `${entry?.label ?? adapterName} is not installed. Install the extension and refresh.`
          );
        }

        await prepareWalletConnection(adapterName);

        // Select first so WalletProvider tracks connect events (React connected/publicKey stay in sync).
        select(adapterName);
        await new Promise((r) => setTimeout(r, 50));

        if (!adapter.connected) {
          await adapter.connect();
        }

        // Soft check for Brave/Nightly — only hard-fails on clear network mismatch.
        await validateWalletNetwork(adapter, connection);

        const address = adapter.publicKey?.toBase58();
        if (!address) {
          throw new Error('Wallet did not return a public key');
        }

        const provider =
          catalogEntryForAdapterName(adapterName)?.slug ??
          String(adapterName).toLowerCase().replace(/\s+/g, '_');

        const message = buildOwnershipMessage(address, provider);

        if (!isMessageSignerAdapter(adapter)) {
          throw new Error('This wallet does not support message signing');
        }

        const proof = await signWalletOwnershipMessage(adapter, message, address);
        const encoded = encodeSignatureForApi(proof.signature);
        // Capture old wallet IDs before linking so we can remove the previous wallet after
        const oldWalletIds = savedWallets
          .filter((w) => w.wallet_id && w.address !== address)
          .map((w) => w.wallet_id as string);

        const result = await linkWallet({
          address,
          message,
          signature: encoded.signature,
          signature_base58: encoded.signature_base58,
          signed_message: proof.signedMessage ? Array.from(proof.signedMessage) : undefined,
          wallet_provider: provider,
          set_primary: true,
        });

        // Remove every previously saved wallet so only the new one remains
        for (const oldId of oldWalletIds) {
          try { await removeWallet(oldId); } catch { /* ignore — already removed */ }
        }

        // Keep only the newly linked wallet in local state
        const cleanWallets = result.wallets.filter((w) => w.address === address);

        setSavedWallets(cleanWallets);
        setPrimaryWallet(pickPrimary(cleanWallets));
        if (result.warning) setWarning(result.warning);
        setSuccessMessage('Wallet connected and saved to your CWY account.');
        setPickerOpen(false);
      } catch (e: unknown) {
        const formatted = formatConnectError(e);
        setPickerError(formatted);
        setError(formatted.detail ? `${formatted.title}: ${formatted.detail}` : formatted.title);
        try {
          if (adapterInstance && 'disconnect' in adapterInstance) {
            await adapterInstance.disconnect();
          } else {
            await adapterDisconnect();
          }
        } catch {
          // ignore
        }
      } finally {
        setConnecting(false);
        setConnectingWallet(null);
      }
    },
    [adapterWallets, select, adapterDisconnect, connection, savedWallets]
  );

  const openPicker = useCallback(() => {
    setError(null);
    setPickerError(null);
    setPickerOpen(true);
  }, []);

  const closePicker = useCallback(() => {
    if (!connecting) {
      setPickerOpen(false);
      setPickerError(null);
    }
  }, [connecting]);

  const clearPickerError = useCallback(() => {
    setPickerError(null);
  }, []);

  const ensurePrimaryWalletConnected = useCallback(async (): Promise<WalletTransactionSigner> => {
    const target = primaryWallet;
    if (!target?.address) {
      throw new Error('No linked wallet on your account');
    }

    if (publicKey?.toBase58() === target.address && connected) {
      const adapter = activeAdapter?.adapter;
      if (adapter && adapterSupportsSending(adapter)) {
        return {
          publicKey,
          sendTransaction: (transaction, connection, options) =>
            sendTransactionWithAdapter(adapter, transaction, connection, options),
        };
      }
      return {
        publicKey,
        sendTransaction: (transaction, connection, options) =>
          sendTransaction(transaction, connection, options),
      };
    }

    const provider = target.wallet_provider ?? 'phantom';
    const catalogEntry = catalogEntryForProviderSlug(provider);
    if (!catalogEntry) {
      throw new Error(`${providerLabel(provider)} is not available in this browser`);
    }

    setConnecting(true);
    setConnectingWallet(catalogEntry.adapterName);
    setError(null);

    try {
      const adapter = getCwyWalletAdapter(catalogEntry.adapterName);
      if (!adapter) {
        throw new Error('Wallet adapter not found');
      }

      if (adapter.readyState === WalletReadyState.NotDetected) {
        throw new Error(
          catalogEntry.detectHint ??
            `${catalogEntry.label} is not installed. Install the extension and refresh.`
        );
      }

      await prepareWalletConnection(catalogEntry.adapterName);
      select(catalogEntry.adapterName);
      await new Promise((r) => setTimeout(r, 50));

      if (!adapter.connected || adapter.publicKey?.toBase58() !== target.address) {
        await adapter.connect();
      }

      const address = adapter.publicKey?.toBase58();
      if (!address || address !== target.address) {
        throw new Error(
          `Switch to ${shortenAddress(target.address)} in ${providerLabel(provider)} to continue.`
        );
      }

      if (!adapterSupportsSending(adapter)) {
        throw new Error('This wallet does not support sending transactions');
      }

      return {
        publicKey: adapter.publicKey!,
        sendTransaction: (transaction, connection, options) =>
          sendTransactionWithAdapter(adapter, transaction, connection, options),
      };
    } catch (e: unknown) {
      const formatted = formatConnectError(e);
      const message = formatted.detail ? `${formatted.title}: ${formatted.detail}` : formatted.title;
      setError(message);
      throw new Error(message);
    } finally {
      setConnecting(false);
      setConnectingWallet(null);
    }
  }, [primaryWallet, publicKey, connected, select, sendTransaction, activeAdapter]);

  const disconnectPrimary = useCallback(async () => {
    if (!primaryWallet?.wallet_id && !primaryWallet?.address) return;

    setDisconnecting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      try {
        await adapterDisconnect();
      } catch {
        // extension may already be disconnected
      }

      const wallets = await removeWallet(primaryWallet.wallet_id);
      setSavedWallets(wallets);
      setPrimaryWallet(pickPrimary(wallets));
      setWarning(null);
      setSuccessMessage('Wallet removed from your CWY account.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || 'Failed to remove wallet');
    } finally {
      setDisconnecting(false);
    }
  }, [primaryWallet, adapterDisconnect]);

  const removeLinkedWallet = useCallback(async (walletId: string) => {
    setRemovingWalletId(walletId);
    setError(null);
    setSuccessMessage(null);

    try {
      const wallets = await removeWallet(walletId);
      setSavedWallets(wallets);
      setPrimaryWallet(pickPrimary(wallets));
      setSuccessMessage('Wallet removed.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || 'Failed to remove wallet');
    } finally {
      setRemovingWalletId(null);
    }
  }, []);

  const makePrimary = useCallback(
    async (walletId: string) => {
      const target = savedWallets.find((w) => w.wallet_id === walletId);
      if (!target?.wallet_id) {
        setError('Wallet not found');
        return;
      }

      setSettingPrimaryId(walletId);
      setError(null);
      setSuccessMessage(null);

      const provider = target.wallet_provider ?? 'phantom';
      const catalogEntry = catalogEntryForProviderSlug(provider);
      let adapterInstance: Adapter | null = null;

      try {
        const adapter = catalogEntry ? getCwyWalletAdapter(catalogEntry.adapterName) : undefined;

        if (!adapter) {
          throw new Error(`${providerLabel(provider)} is not available in this browser`);
        }

        adapterInstance = adapter;

        await prepareWalletConnection(catalogEntry!.adapterName);
        if (!adapter.connected || adapter.publicKey?.toBase58() !== target.address) {
          await adapter.connect();
        }

        const address = adapter.publicKey?.toBase58();
        if (address !== target.address) {
          throw new Error(
            `Switch to ${shortenAddress(target.address)} in ${providerLabel(provider)} to confirm this change.`
          );
        }

        if (!isMessageSignerAdapter(adapter)) {
          throw new Error('This wallet does not support message signing');
        }

        const message = buildOwnershipMessage(address, provider);
        const proof = await signWalletOwnershipMessage(adapter, message, address);
        const encoded = encodeSignatureForApi(proof.signature);

        const wallets = await setPrimaryWalletApi({
          walletId: target.wallet_id,
          message,
          signature: encoded.signature,
          signature_base58: encoded.signature_base58,
          signed_message: proof.signedMessage ? Array.from(proof.signedMessage) : undefined,
          wallet_provider: provider,
        });

        setSavedWallets(wallets);
        setPrimaryWallet(pickPrimary(wallets));
        setSuccessMessage('Primary wallet updated.');
        select(catalogEntry!.adapterName);
      } catch (e: unknown) {
        const formatted = formatConnectError(e);
        setError(formatted.detail ? `${formatted.title}: ${formatted.detail}` : formatted.title);
        try {
          if (adapterInstance && 'disconnect' in adapterInstance) {
            await adapterInstance.disconnect();
          }
        } catch {
          // ignore
        }
      } finally {
        setSettingPrimaryId(null);
      }
    },
    [savedWallets, adapterWallets, select]
  );

  const clearMessages = useCallback(() => {
    setError(null);
    setWarning(null);
    setSuccessMessage(null);
    setPickerError(null);
  }, []);

  return {
    adapterWallets,
    activeAdapter,
    connected,
    connectedAddress,
    savedWallets,
    savedWallet,
    primaryWallet,
    linkStatus,
    connecting: connecting || adapterConnecting,
    connectingWallet,
    disconnecting,
    settingPrimaryId,
    removingWalletId,
    loadingSaved,
    error,
    pickerError,
    warning,
    successMessage,
    pickerOpen,
    openPicker,
    closePicker,
    clearPickerError,
    connectWithAdapter,
    ensurePrimaryWalletConnected,
    disconnect: disconnectPrimary,
    removeLinkedWallet,
    makePrimary,
    refreshSavedWallet: loadSavedWallets,
    clearMessages,
    isPhantomInstalled: adapterWallets.some(
      (w) => w.adapter.name === ('Phantom' as WalletName) && w.readyState === WalletReadyState.Installed
    ),
    phantomAddress: connectedAddress,
    connect: openPicker,
  };
}
